import React, { useEffect, useMemo, useState, useRef, Fragment, createElement, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { DownloadIcon, CalendarIcon, ChevronRightIcon, ChevronDownIcon, FileTextIcon, InfoIcon } from 'lucide-react';
import { getTenant, formatCurrencyAmount } from '../../../theme/tokens';
import { getAllPayouts, type Payout, type PayoutTransfer, type PayoutStatus, type TransferStatus } from '../mocks/payoutsData';
const PAGE_SIZE = 9;
const formatCurrency = (amount: number) => {
  return formatCurrencyAmount(amount, {
    fractionDigits: 2
  });
};
function getPayoutStatusBadge(status: PayoutStatus) {
  const styles: Record<PayoutStatus, string> = {
    submitted: 'bg-[var(--colors-info-light)] text-[var(--colors-info-dark)] border-[var(--colors-info-default)]',
    partially_submitted: 'bg-[var(--colors-warning-light)] text-[var(--colors-warning-dark)] border-[var(--colors-warning-default)]',
    failed: 'bg-[var(--colors-error-light)] text-[var(--colors-error-dark)] border-[var(--colors-error-default)]',
    cancelled: 'bg-[var(--colors-neutral-50)] text-[var(--colors-neutral-600)] border-[var(--colors-neutral-200)]',
    pending: 'bg-[var(--colors-neutral-50)] text-[var(--colors-neutral-600)] border-[var(--colors-neutral-200)]'
  };
  const labels: Record<PayoutStatus, string> = {
    submitted: 'Submitted',
    partially_submitted: 'Partially submitted',
    failed: 'Failed',
    cancelled: 'Cancelled',
    pending: 'Pending'
  };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status]}`} data-id="element-3234">
      {labels[status]}
    </span>;
}
function getTransferStatusBadge(status: TransferStatus) {
  const styles: Record<TransferStatus, string> = {
    submitted: 'bg-[var(--colors-info-light)] text-[var(--colors-info-dark)] border-[var(--colors-info-default)]',
    failed: 'bg-[var(--colors-error-light)] text-[var(--colors-error-dark)] border-[var(--colors-error-default)]',
    cancelled: 'bg-[var(--colors-neutral-50)] text-[var(--colors-neutral-600)] border-[var(--colors-neutral-200)]',
    pending: 'bg-[var(--colors-neutral-50)] text-[var(--colors-neutral-600)] border-[var(--colors-neutral-200)]'
  };
  const labels: Record<TransferStatus, string> = {
    submitted: 'Submitted',
    failed: 'Failed',
    cancelled: 'Cancelled',
    pending: 'Pending'
  };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status]}`} data-id="element-3235">
      {labels[status]}
    </span>;
}
// Shared CSV escape utility
function escCsv(val: string | number): string {
  const s = String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}
function downloadBlob(filename: string, csvContent: string) {
  const blob = new Blob([csvContent], {
    type: 'text/csv;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
// ─── Report 1: Payout Summary (one row per payout) ───
function downloadPayoutSummaryCsv(payouts: Payout[]) {
  const headers = ['Payout ID', 'Merchant Entity', 'Payout Status', 'Effective Date', 'Currency', 'Gross Amount', 'Total Deductions', 'Net Amount', 'Destination Account', 'Number of Transfers'];
  const rows = payouts.map(p => [escCsv(p.payoutId), escCsv(p.merchantEntity), escCsv(p.status), escCsv(p.effectiveDate), escCsv(p.currency), p.grossAmount.toFixed(2), p.totalDeductions.toFixed(2), p.netAmount.toFixed(2), escCsv(p.destinationAccount), p.transfers.length].join(','));
  const brandName = getTenant().brand.name.toLowerCase();
  downloadBlob(`${brandName}-payout-summary-${new Date().toISOString().slice(0, 10)}.csv`, '\uFEFF' + [headers.join(','), ...rows].join('\n'));
}
// ─── Report 2: Payout Transfer Report (one row per bank transfer) ───
function downloadPayoutTransferCsv(payouts: Payout[]) {
  const headers = ['Payout ID', 'Transfer ID', 'Transfer Status', 'Effective Date', 'Currency', 'Gross Amount', 'Total Deductions', 'Net Amount', 'Bank Reference', 'Destination Account', 'Description'];
  const rows: string[] = [];
  payouts.forEach(p => {
    p.transfers.forEach(t => {
      rows.push([escCsv(p.payoutId), escCsv(t.transferId), escCsv(t.status), escCsv(p.effectiveDate), escCsv(p.currency), t.grossAmount.toFixed(2), t.totalDeductions.toFixed(2), t.netAmount.toFixed(2), escCsv(t.bankReference), escCsv(p.destinationAccount), escCsv(t.description)].join(','));
    });
  });
  const brandName = getTenant().brand.name.toLowerCase();
  downloadBlob(`${brandName}-payout-transfers-${new Date().toISOString().slice(0, 10)}.csv`, '\uFEFF' + [headers.join(','), ...rows].join('\n'));
}
// ─── Report 3: Payout Detail Report (one row per transaction line item) ───
interface PayoutTransaction {
  activityType: 'Payment' | 'Refund' | 'Fee' | 'Chargeback' | 'Adjustment';
  sourceActivityId: string;
  currency: string;
  grossAmount: number;
  fee: number;
  netAmount: number;
  description: string;
  orderId: string;
  merchantReferenceId: string;
}
function generateMockTransactions(payout: Payout): PayoutTransaction[] {
  const seed = payout.payoutId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const rng = (i: number) => {
    const x = Math.sin(seed + i) * 10000;
    return x - Math.floor(x);
  };
  const txns: PayoutTransaction[] = [];
  let remainingGross = payout.grossAmount;
  let remainingFees = payout.totalDeductions;
  const descs = ['Weekly grocery order - fresh produce', 'Bulk rice and noodles purchase', 'Seafood platter catering order', 'Office lunch delivery - 25 pax', 'Monthly subscription box - premium', 'Fruit basket gift set x3', 'Restaurant supply restock', 'Organic vegetables wholesale', 'Frozen goods monthly order', 'Beverage supply - soft drinks', 'Bakery ingredients wholesale', 'Meat and poultry weekly supply', 'Dairy products - milk and cheese', 'Cooking oil and condiments restock', 'Hotel breakfast buffet supply', 'School canteen weekly provisions'];
  const count = 8 + Math.floor(rng(0) * 8);
  const base = payout.grossAmount / count;
  for (let i = 0; i < count; i++) {
    const isLast = i === count - 1;
    const gross = isLast ? remainingGross : Math.round(base * (0.5 + rng(i + 10)) * 100) / 100;
    const actualGross = Math.max(100, Math.min(gross, remainingGross));
    const fee = isLast ? remainingFees : Math.round(actualGross * (0.029 + rng(i + 50) * 0.005) * 100) / 100;
    remainingGross = Math.round((remainingGross - actualGross) * 100) / 100;
    remainingFees = Math.round((remainingFees - fee) * 100) / 100;
    const num = String(10000 + Math.floor(rng(i + 20) * 90000));
    txns.push({
      activityType: 'Payment',
      sourceActivityId: `pay_${num}`,
      currency: payout.currency,
      grossAmount: actualGross,
      fee,
      netAmount: Math.round((actualGross - fee) * 100) / 100,
      description: descs[Math.floor(rng(i + 60) * descs.length)],
      orderId: `ord_${String(10000 + Math.floor(rng(i + 30) * 90000))}`,
      merchantReferenceId: `MR-${String(1000 + Math.floor(rng(i + 40) * 9000))}`
    });
  }
  // Add 1-2 refunds
  const refundCount = 1 + Math.floor(rng(100) * 2);
  for (let i = 0; i < refundCount; i++) {
    const amt = Math.round((500 + rng(i + 200) * 5000) * 100) / 100;
    const num = String(10000 + Math.floor(rng(i + 210) * 90000));
    txns.push({
      activityType: 'Refund',
      sourceActivityId: `rf_${num}`,
      currency: payout.currency,
      grossAmount: -amt,
      fee: 0,
      netAmount: -amt,
      description: 'Refund - customer return',
      orderId: txns[Math.floor(rng(i + 220) * Math.min(3, txns.length))]?.orderId || `ord_${num}`,
      merchantReferenceId: txns[0]?.merchantReferenceId || `MR-${num}`
    });
  }
  return txns;
}
function downloadPayoutDetailCsv(payouts: Payout[]) {
  const headers = ['Payout ID', 'Activity Type', 'Source Activity ID', 'Currency', 'Gross Amount', 'Fee', 'Net Amount', 'Description', 'Order ID', 'Merchant Reference ID'];
  const rows: string[] = [];
  payouts.forEach(p => {
    const txns = generateMockTransactions(p);
    txns.forEach(t => {
      rows.push([escCsv(p.payoutId), escCsv(t.activityType), escCsv(t.sourceActivityId), escCsv(t.currency), t.grossAmount.toFixed(2), t.fee.toFixed(2), t.netAmount.toFixed(2), escCsv(t.description), escCsv(t.orderId), escCsv(t.merchantReferenceId)].join(','));
    });
  });
  const brandName = getTenant().brand.name.toLowerCase();
  downloadBlob(`${brandName}-payout-detail-${new Date().toISOString().slice(0, 10)}.csv`, '\uFEFF' + [headers.join(','), ...rows].join('\n'));
}
// ─── Report Dropdown Component ───
function ReportDropdown({
  label,
  onSummary,
  onTransfer,
  onDetail
}: {
  label: string;
  onSummary: () => void;
  onTransfer: () => void;
  onDetail: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  return <div ref={ref} className="relative inline-block" data-id="element-3236">
      <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors" data-id="element-3237">
        <DownloadIcon size={12} data-id="element-3238" />
        {label}
        <ChevronDownIcon size={10} data-id="element-3239" />
      </button>

      <AnimatePresence data-id="element-3240">
        {open && <motion.div initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.12
      }} className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1" data-id="element-3241">
            <button onClick={() => {
          onSummary();
          setOpen(false);
        }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5" data-id="element-3242">
              <FileTextIcon size={14} className="text-gray-400" data-id="element-3243" />
              <div data-id="element-3244">
                <div className="font-medium" data-id="element-3245">Payout summary</div>
                <div className="text-xs text-gray-500" data-id="element-3246">One row per payout</div>
              </div>
            </button>
            <button onClick={() => {
          onTransfer();
          setOpen(false);
        }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5" data-id="element-3247">
              <FileTextIcon size={14} className="text-gray-400" data-id="element-3248" />
              <div data-id="element-3249">
                <div className="font-medium" data-id="element-3250">Transfer report</div>
                <div className="text-xs text-gray-500" data-id="element-3251">
                  One row per bank transfer
                </div>
              </div>
            </button>
            <button onClick={() => {
          onDetail();
          setOpen(false);
        }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5" data-id="element-3252">
              <FileTextIcon size={14} className="text-gray-400" data-id="element-3253" />
              <div data-id="element-3254">
                <div className="font-medium" data-id="element-3255">Payout detail</div>
                <div className="text-xs text-gray-500" data-id="element-3256">
                  One row per transaction
                </div>
              </div>
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function StatusTooltipHeader() {
  const [isVisible, setIsVisible] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState({
    top: 0,
    left: 0
  });
  useEffect(() => {
    if (isVisible && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2
      });
    }
  }, [isVisible]);
  return <div className="flex items-center gap-1.5" data-id="element-3257">
      Status
      <button ref={buttonRef} type="button" className="text-gray-400 hover:text-gray-500 focus:outline-none" onMouseEnter={() => setIsVisible(true)} onMouseLeave={() => setIsVisible(false)} onFocus={() => setIsVisible(true)} onBlur={() => setIsVisible(false)} data-id="element-3258">
        <InfoIcon size={13} data-id="element-3259" />
      </button>
      {isVisible && createPortal(<div style={{
      top: pos.top,
      left: pos.left
    }} className="fixed -translate-x-1/2 w-72 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-[9999] font-normal normal-case tracking-normal leading-relaxed pointer-events-none" data-id="element-3260">
            <p className="font-semibold mb-2 text-[11px] uppercase tracking-wider text-gray-300" data-id="element-3261">
              Payout statuses
            </p>
            <ul className="space-y-1.5 mb-3" data-id="element-3262">
              <li data-id="element-3263">
                <span className="font-medium text-white" data-id="element-3264">Pending</span> —
                Created but not yet submitted to the bank
              </li>
              <li data-id="element-3265">
                <span className="font-medium text-white" data-id="element-3266">Submitted</span> — All
                transfers submitted to the bank
              </li>
              <li data-id="element-3267">
                <span className="font-medium text-white" data-id="element-3268">
                  Partially submitted
                </span>{' '}
                — Some transfers submitted, others pending or failed
              </li>
              <li data-id="element-3269">
                <span className="font-medium text-white" data-id="element-3270">Failed</span> —
                Submission failed
              </li>
              <li data-id="element-3271">
                <span className="font-medium text-white" data-id="element-3272">Cancelled</span> —
                Cancelled before submission completed
              </li>
            </ul>

            <p className="font-semibold mb-2 text-[11px] uppercase tracking-wider text-gray-300" data-id="element-3273">
              Transfer statuses
            </p>
            <ul className="space-y-1.5" data-id="element-3274">
              <li data-id="element-3275">
                <span className="font-medium text-white" data-id="element-3276">Pending</span> —
                Created but not yet submitted
              </li>
              <li data-id="element-3277">
                <span className="font-medium text-white" data-id="element-3278">Submitted</span> —
                Instruction submitted to the bank
              </li>
              <li data-id="element-3279">
                <span className="font-medium text-white" data-id="element-3280">Failed</span> —
                Submission failed
              </li>
              <li data-id="element-3281">
                <span className="font-medium text-white" data-id="element-3282">Cancelled</span> —
                Cancelled before submission
              </li>
            </ul>
          </div>, document.body)}
    </div>;
}
export function Payouts() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedPayouts, setExpandedPayouts] = useState<Set<string>>(new Set());
  const allPayouts = useMemo(() => getAllPayouts(), []);
  const totalEntries = allPayouts.length;
  const totalPages = Math.ceil(totalEntries / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, totalEntries);
  const currentEntries = allPayouts.slice(startIndex, endIndex);
  const last7DaysPayouts = allPayouts.filter(p => {
    const dateStr = p.effectiveDate;
    return dateStr.includes('May 7') || dateStr.includes('May 6') || dateStr.includes('May 5') || dateStr.includes('May 4') || dateStr.includes('May 3') || dateStr.includes('May 2') || dateStr.includes('May 1');
  });
  const last7Total = last7DaysPayouts.reduce((sum, p) => sum + p.grossAmount, 0);
  const last30Total = allPayouts.reduce((sum, p) => sum + p.grossAmount, 0);
  const toggleExpand = (payoutId: string) => {
    setExpandedPayouts(prev => {
      const next = new Set(prev);
      if (next.has(payoutId)) {
        next.delete(payoutId);
      } else {
        next.add(payoutId);
      }
      return next;
    });
  };
  return <div className="max-w-[1600px] mx-auto" data-id="element-3283">
      {/* Header */}
      <div className="flex items-center justify-between mb-8" data-id="element-3284">
        <h1 className="text-3xl font-bold text-gray-900" data-id="element-3285">Payouts</h1>
        <ReportDropdown label="Export" onSummary={() => downloadPayoutSummaryCsv(allPayouts)} onTransfer={() => downloadPayoutTransferCsv(allPayouts)} onDetail={() => downloadPayoutDetailCsv(allPayouts)} data-id="element-3286" />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6" data-id="element-3287">
        <div className="bg-white border border-gray-200 rounded-lg p-5" data-id="element-3288">
          <div className="flex items-center justify-between mb-3" data-id="element-3289">
            <span className="text-sm text-gray-500" data-id="element-3290">Next 7 days</span>
            <CalendarIcon size={16} className="text-gray-400" data-id="element-3291" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1" data-id="element-3292">
            {formatCurrency(allPayouts.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.grossAmount, 0))}
          </div>
          <div className="text-sm text-gray-500" data-id="element-3293">
            {allPayouts.filter(p => p.status === 'pending').length} upcoming
            payout
            {allPayouts.filter(p => p.status === 'pending').length !== 1 ? 's' : ''}{' '}
            · May 6 – May 12, 2026
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-5" data-id="element-3294">
          <div className="flex items-center justify-between mb-3" data-id="element-3295">
            <span className="text-sm text-gray-500" data-id="element-3296">Last 7 days</span>
            <CalendarIcon size={16} className="text-gray-400" data-id="element-3297" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1" data-id="element-3298">
            {formatCurrency(last7Total)}
          </div>
          <div className="text-sm text-gray-500" data-id="element-3299">
            {last7DaysPayouts.length} payouts · May 1 – May 7, 2026
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-5" data-id="element-3300">
          <div className="flex items-center justify-between mb-3" data-id="element-3301">
            <span className="text-sm text-gray-500" data-id="element-3302">Last 30 days</span>
            <CalendarIcon size={16} className="text-gray-400" data-id="element-3303" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1" data-id="element-3304">
            {formatCurrency(last30Total)}
          </div>
          <div className="text-sm text-gray-500" data-id="element-3305">
            {allPayouts.length} payouts · Apr 7 – May 6, 2026
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-3306">
        <div className="overflow-x-auto" data-id="element-3307">
          <table className="w-full" data-id="element-3308">
            <thead data-id="element-3309">
              <tr className="border-b border-gray-200 bg-gray-50/50" data-id="element-3310">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap w-10" data-id="element-3311"></th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3312">
                  Payout ID
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3313">
                  <StatusTooltipHeader data-id="element-3314" />
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3315">
                  Effective date
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3316">
                  Currency
                </th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3317">
                  Gross amount
                </th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3318">
                  Deductions
                </th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3319">
                  Net amount
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3320">
                  Destination
                </th>
                <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3321">
                  Transfers
                </th>
                <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3322">
                  Report
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-3323">
              {currentEntries.map(payout => {
              const isExpanded = expandedPayouts.has(payout.payoutId);
              return <Fragment key={payout.payoutId} data-id="element-3324">
                    {/* Payout Row — clickable to navigate to detail */}
                    <tr onClick={() => navigate(`/payouts/${payout.payoutId}`)} className={`hover:bg-gray-50/50 transition-colors cursor-pointer ${isExpanded ? 'bg-gray-50/30' : ''}`} data-id="element-3325">
                      <td className="px-5 py-4" data-id="element-3326">
                        <button onClick={e => {
                      e.stopPropagation();
                      toggleExpand(payout.payoutId);
                    }} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label={isExpanded ? 'Collapse transfers' : 'Expand transfers'} data-id="element-3327">
                          <motion.div animate={{
                        rotate: isExpanded ? 90 : 0
                      }} transition={{
                        duration: 0.15
                      }} data-id="element-3328">
                            <ChevronRightIcon size={16} data-id="element-3329" />
                          </motion.div>
                        </button>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-900 font-mono font-medium whitespace-nowrap" data-id="element-3330">
                        {payout.payoutId}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap" data-id="element-3331">
                        {getPayoutStatusBadge(payout.status)}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700 whitespace-nowrap" data-id="element-3332">
                        {payout.effectiveDate}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700 whitespace-nowrap" data-id="element-3333">
                        {payout.currency}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-900 text-right whitespace-nowrap" data-id="element-3334">
                        {formatCurrency(payout.grossAmount)}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700 text-right whitespace-nowrap" data-id="element-3335">
                        {formatCurrency(payout.totalDeductions)}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-900 font-semibold text-right whitespace-nowrap" data-id="element-3336">
                        {formatCurrency(payout.netAmount)}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700 whitespace-nowrap" data-id="element-3337">
                        {payout.destinationAccount}
                      </td>
                      <td className="px-5 py-4 text-center whitespace-nowrap" data-id="element-3338">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-xs font-medium text-gray-600" data-id="element-3339">
                          {payout.transfers.length}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-center whitespace-nowrap" data-id="element-3340">
                        <button onClick={e => {
                      e.stopPropagation();
                      downloadPayoutDetailCsv([payout]);
                    }} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors" data-id="element-3341">
                          <DownloadIcon size={12} data-id="element-3342" />
                          .csv
                        </button>
                      </td>
                    </tr>

                    {/* Transfer Sub-Rows */}
                    <AnimatePresence data-id="element-3343">
                      {isExpanded && payout.transfers.map((transfer, idx) => <motion.tr key={transfer.transferId} onClick={e => e.stopPropagation()} initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} transition={{
                    duration: 0.15,
                    delay: idx * 0.03
                  }} className="bg-[var(--colors-neutral-50)] border-l-2 border-l-[var(--colors-border-default)]" data-id="element-3344">
                            <td className="px-5 py-3" data-id="element-3345"></td>
                            <td className="px-5 py-3 text-sm font-mono text-[var(--colors-text-secondary)] whitespace-nowrap" data-id="element-3346">
                              {transfer.transferId}
                            </td>
                            <td className="px-5 py-3 whitespace-nowrap" data-id="element-3347">
                              {getTransferStatusBadge(transfer.status)}
                            </td>
                            <td className="px-5 py-3" data-id="element-3348"></td>
                            <td className="px-5 py-3" data-id="element-3349"></td>
                            <td className="px-5 py-3 text-sm text-[var(--colors-text-secondary)] text-right whitespace-nowrap" data-id="element-3350">
                              {formatCurrency(transfer.grossAmount)}
                            </td>
                            <td className="px-5 py-3 text-sm text-[var(--colors-text-tertiary)] text-right whitespace-nowrap" data-id="element-3351">
                              {formatCurrency(transfer.totalDeductions)}
                            </td>
                            <td className="px-5 py-3 text-sm text-[var(--colors-text-primary)] font-medium text-right whitespace-nowrap" data-id="element-3352">
                              {formatCurrency(transfer.netAmount)}
                            </td>
                            <td className="px-5 py-3" data-id="element-3353"></td>
                            <td className="px-5 py-3 text-xs font-mono text-[var(--colors-text-tertiary)] whitespace-nowrap" data-id="element-3354">
                              {transfer.bankReference}
                            </td>
                            <td className="px-5 py-3" data-id="element-3355"></td>
                          </motion.tr>)}
                    </AnimatePresence>
                  </Fragment>;
            })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200" data-id="element-3356">
          <span className="text-sm text-gray-500" data-id="element-3357">
            Showing {startIndex + 1} to {endIndex} of {totalEntries} payouts
          </span>
          <div className="flex items-center gap-2" data-id="element-3358">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-3359">
              Previous
            </button>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-3360">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>;
}